import { useEffect, useState } from 'react';
import { Settings, Clock, Mail } from 'lucide-react';
import api from '../../lib/api';

export const MaintenancePage = () => {
  const [settings, setSettings] = useState({
    maintenance_title: 'Sito in Manutenzione',
    maintenance_message: 'Stiamo lavorando per migliorare la tua esperienza. Torna a trovarci presto!',
    maintenance_end_date: null,
    seo_site_title: '',
    seo_site_image: ''
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/settings/maintenance');
        if (res.data) {
          setSettings(prev => ({ ...prev, ...res.data }));
        }
      } catch (error) {
        console.error('Error fetching maintenance settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('it-IT', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return null;
    }
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)'
      }}
      data-testid="maintenance-page"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-primary/5 to-purple-500/5 rounded-full blur-3xl" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-2xl mx-auto text-center">
        {/* Logo/Icon */}
        <div className="mb-8 flex justify-center">
          <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-500/20 border border-white/10 flex items-center justify-center backdrop-blur-sm">
            <Settings className="w-12 h-12 text-primary animate-spin-slow" style={{ animationDuration: '8s' }} />
          </div>
        </div>

        {/* Site Title */}
        {settings.seo_site_title && (
          <p className="text-sm uppercase tracking-widest text-primary/80 mb-4 font-medium">
            {settings.seo_site_title}
          </p>
        )}

        {/* Main Title */}
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
          {settings.maintenance_title}
        </h1>

        {/* Message */}
        <p className="text-lg sm:text-xl text-white/70 mb-8 leading-relaxed max-w-xl mx-auto">
          {settings.maintenance_message}
        </p>

        {/* End Date */}
        {settings.maintenance_end_date && (
          <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-8">
            <Clock className="w-5 h-5 text-primary" />
            <span className="text-white/80 text-sm">
              Torneremo online: <strong className="text-white">{formatDate(settings.maintenance_end_date)}</strong>
            </span>
          </div>
        )}

        {/* Decorative Divider */}
        <div className="flex items-center justify-center gap-4 my-8">
          <div className="h-px w-16 bg-gradient-to-r from-transparent to-white/20" />
          <div className="w-2 h-2 rounded-full bg-primary/50" />
          <div className="h-px w-16 bg-gradient-to-l from-transparent to-white/20" />
        </div>

        {/* Footer Info */}
        <p className="text-sm text-white/40">
          Grazie per la pazienza. Il nostro team sta lavorando duramente per offrirti un'esperienza migliore.
        </p>
      </div>

      {/* CSS for slow spin animation */}
      <style>{`
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
      `}</style>
    </div>
  );
};
